import { Button, Spinner } from "react-bootstrap";
import { useState } from 'react';
import { Match, MatchStatus } from "../../../models/interfaces/Match";
import { CompetitionFilterProps, CompetitionFilterView } from "./CompetitionPickerViews";
import { useMatchScore } from "../../../helpers/useMatchStats";
import { SportProvider } from "../../../models/interfaces/SportProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import styled from "styled-components";
import { MatchEntry } from "../../../models/interfaces/MatchEntry";


import { MatchPlaceholders } from './FeaturedEventPlaceholder';

interface Props {
    sportProvider: SportProvider,
    filter: CompetitionFilterProps;
    matches: Match[] | undefined;
    noContentText: string;
    loading?: boolean;
}

export function EventMatchDisplayView({ sportProvider, filter, matches, noContentText, loading }: Props) {
    const selectedCompetitionEntry = filter.teamSelect.selectedCompetitionEntry;
    const [visibleCount, setVisibleCount] = useState(25);
    const filteredMatches = matches?.filter(match => !selectedCompetitionEntry || match.lightCapTeam.id === selectedCompetitionEntry.id || match.darkCapTeam.id === selectedCompetitionEntry.id);
    return (
        <div style={{ paddingTop: '10px' }}>
            <CompetitionFilterView
                competitionSelect={filter.competitionSelect}
                teamSelect={filter.teamSelect}
            />
            {loading && !matches?.length && <MatchPlaceholders />}
            <div style={{ width: '100%', paddingTop: '10px' }}>
                {matches ? (
                    matches.length > 0 ? (
                        <div className="matchContainer">
                            {filteredMatches?.slice(0, visibleCount).map((match) => (
                                <div className="matchElement" key={match.id}>
                                    <a
                                        href={`/matches/${match.id}`}>
                                        <MatchDisplay sportProvider={sportProvider} match={match} />
                                    </a>
                                </div>
                            ))}
                            {(filteredMatches?.length || 0) > visibleCount && <Button variant="link" onClick={() => setVisibleCount(count => count + 25)}>Show more matches</Button>}
                            {filteredMatches?.length === 0 && !loading && <div>No matches for this team.</div>}
                        </div>
                    ) : (
                        <div className="matchContainer">{!loading && noContentText}</div>
                    )
                ) : (
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                )}
            </div>
        </div>
    );
}

interface MatchDisplayProps {
    sportProvider: SportProvider,
    match: Match;
}

const FlexibleContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: stretch;
    
    padding: 10px;
    gap: 10px;
    min-height: 66px;

    /* The page container caps the card well below the screen width,
       so these breakpoints follow the card width, not the screen width. */
    @media only screen and (max-width: 767px) {
        flex-direction: column;
        gap: 5px;
        padding: 5px;
    }
`

const CompetitionDetailWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 170px;
    min-width: 170px;
    padding-left: 8px;
    @media only screen and (max-width: 991px) {
        display: none;
    }
`;

const LargeScreenVenueView = styled.div`
    display: flex; 
    flex-direction: column;
    justify-content: center;
    align-items: center;

    width: 160px;
    min-width: 160px;

    @media only screen and (max-width: 767px) {
        display: none;
    }
`

const ButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 78px;
    min-width: 78px;
    @media only screen and (max-width: 767px) {
        display: none;
    }
`;

function MatchDisplay({ sportProvider, match }: MatchDisplayProps) {
    const { lightTeamScore, darkTeamScore } = useMatchScore({ match });
    const isLive = match.status === MatchStatus.inProgress;
    const isComplete = match.status === MatchStatus.completed;

    return <FlexibleContainer>
        <MatchMetadataView match={match} />
        <TeamScoreWrapper className="disclosureIndicator">
            <TeamScoreView 
                lightCapTeam={match.lightCapTeam} darkCapTeam={match.darkCapTeam} 
                lightCapScore={lightTeamScore} darkCapScore={darkTeamScore}
            />
        </TeamScoreWrapper>
        <CompetitionDetailWrapper style={{marginRight: 'auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <MetaLine style={{ color: '#d7e3f3', fontSize: '12px' }}>{match.competition.attributes.name}</MetaLine>
                <MetaLine style={{ color: '#b8cae1', fontSize: '12px' }}>{sportProvider.displayTextForTeamOrCompetitionAttributes(match.competition.attributes)}</MetaLine>
            </div>
        </CompetitionDetailWrapper>
        { match.venue &&
            <LargeScreenVenueView style={{marginLeft: 'auto' }}>
                <MetaLine style={{ color: '#d7e3f3', fontSize: '12px', textAlign: 'center' }}>{match.venue.name}</MetaLine>
                <MetaLine style={{ color: '#b8cae1', fontSize: '12px', textAlign: 'center' }}>{match.venue?.subvenueName}</MetaLine>
            </LargeScreenVenueView>
        }
        <ButtonWrapper style={{marginLeft: 'auto'}}>
            <div style={{
                display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
            }}>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                    <span className="disclosureIndicator" style={{ color: isLive ? 'var(--wpa-aqua-light)' : isComplete ? 'var(--wpa-yellow)' : 'var(--wpa-muted)', fontWeight: 700 }}>
                        {isLive ? 'LIVE' : 'Details'}
                    </span>
                    <FontAwesomeIcon icon={faChevronRight as IconProp} style={{ height: '10px', paddingLeft: '4px', paddingBottom: '2px' }} />
                    
                </div>
            </div>
        </ButtonWrapper>
        
    </FlexibleContainer>
}

const TeamScoreWrapper = styled.div`
    flex: 1 1 auto;
    min-width: 0;
`

const TeamScoreContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 10px;
    width: 100%;
    max-width: 320px;

    @media only screen and (max-width: 767px) {
        justify-content: space-between;
        max-width: none;
    }
`

const TeamContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1 1 auto;
    min-width: 0;
`

const ScoreContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 38px;
    align-items: flex-end;
`

const TeamName = styled.span`
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const MetaLine = styled.span`
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

function TeamScoreView({ lightCapTeam, darkCapTeam, lightCapScore, darkCapScore }: {lightCapTeam: MatchEntry, darkCapTeam: MatchEntry, lightCapScore ?: number, darkCapScore ?: number}) {
    const boldLight = typeof lightCapScore === 'number' && typeof darkCapScore === 'number' && lightCapScore > darkCapScore;
    const boldDark = typeof lightCapScore === 'number' && typeof darkCapScore === 'number' && darkCapScore > lightCapScore;
    return <TeamScoreContainer>
        <TeamContainer>
            <TeamName style={{fontWeight: boldLight ? 700 : 500, color: boldLight ? 'var(--wpa-white)' : '#d7e3f3' }}>{lightCapTeam.name}</TeamName>
            <TeamName style={{fontWeight: boldDark ? 700 : 500, color: boldDark ? 'var(--wpa-white)' : '#d7e3f3' }}>{darkCapTeam.name}</TeamName>
        </TeamContainer>
        {
            typeof lightCapScore === 'number' && typeof darkCapScore === 'number' && 
            <ScoreContainer>
                <span style={{fontWeight: boldLight ? 700 : 500, color: boldLight ? 'var(--wpa-yellow)' : '#d7e3f3' }}>{lightCapScore}</span>
                <span style={{fontWeight: boldDark ? 700 : 500, color: boldDark ? 'var(--wpa-yellow)' : '#d7e3f3' }}>{darkCapScore}</span>
            </ScoreContainer>
        }
    </TeamScoreContainer>
}

const MetadataContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;

    min-width: 90px;
    flex-shrink: 0;
    padding-right: 10px;

    border-right: solid;
    border-right-color: rgba(255, 255, 255, 0.15);
    
    @media only screen and (max-width: 767px) {
        flex-direction: column;
        align-items: center;
        border-right: none;
        min-height: 0px;
    }
`

const MetadataItemContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    @media only screen and (max-width: 767px) {
        flex-direction: row;
        border-right: none;
        gap: 10px;
        min-height: 0px;
    }
`

const MetadataVenueView = styled.div`
    display: none;
    
    @media only screen and (max-width: 767px) {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        min-width: 90px;
        gap: 10px;
    }
`

function MatchMetadataView({ match }: { match: Match }) {
    return (
    <MetadataContainer>
        { match.venue &&
            <MetadataVenueView>
                <span style={{ color: 'lightgrey' }}>{match.venue.name}{match.venue.subvenueName ? `, ${match.venue.subvenueName}`: ''}</span>
            </MetadataVenueView>
        }
        <MetadataItemContainer>
            <>
                <span style={{ color: '#d7e3f3' }}>{match.date.toLocaleDateString()}</span>
                <span style={{ color: '#d7e3f3' }}>
                    {match.date.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                    })}
                </span>
            </>
        </MetadataItemContainer>
    </MetadataContainer>
    )
}